import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDT } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import {
  Users, Building2, Layers, Bell, FileUp, Settings2, Plus, Trash2, Edit2, X, Loader2,
  Send, MessageSquare, Mail, ShieldCheck, Save, UploadCloud, CheckCircle2, KeyRound, History,
  ChevronUp, ChevronDown, BellRing, Shield,
} from "lucide-react";

// Tabs with no `adminOnly` flag match a GM_ADMIN_SECTIONS key by their id and
// can be delegated to a non-admin role via Admin → Roles. Everything else
// (adminOnly: true) is administrator-only regardless of role permissions.
const TABS = [
  { id: "users", label: "Users", icon: Users, adminOnly: true },
  { id: "departments", label: "Departments", icon: Building2, adminOnly: true },
  { id: "classifications", label: "Classifications", icon: Layers },
  { id: "notifications", label: "Notifications", icon: Bell, adminOnly: true },
  { id: "alertrouting", label: "Alert Routing", icon: BellRing, adminOnly: true },
  { id: "opera", label: "Opera XML / TXT", icon: FileUp, adminOnly: true },
  { id: "general", label: "General", icon: Settings2, adminOnly: true },
  { id: "roles", label: "Roles", icon: Shield, adminOnly: true },
  { id: "audit", label: "Audit Log", icon: History },
];

const field =
  "w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-[#4e73df] focus:ring-2 focus:ring-[#4e73df]/20 outline-none transition text-sm";
const lbl = "text-[13px] font-semibold text-slate-600 mb-1.5 block";
const card = "bg-white rounded-2xl gm-shadow p-5";

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none ${checked ? "bg-[#4e73df]" : "bg-slate-300"}`}>
      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`} />
    </button>
  );
}

/* ---------------- Users ---------------- */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modal, setModal] = useState(null);
  const empty = { username: "", password: "", department: "", email: "", phone: "", role: "user", active: true };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); api.get("/departments").then(({ data }) => setDepartments(data)); api.get("/roles").then(({ data }) => setRoles(data)); }, []);
  const roleLabel = (name) => (name === "admin" ? "Administrator" : (roles.find((r) => r.name === name)?.label || name));

  const save = async () => {
    try {
      if (modal === "new") await api.post("/users", form);
      else await api.put(`/users/${form.id}`, form);
      toast.success("Saved");
      setModal(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this user?")) return; try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };

  return (
    <div className={card}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-[#3a3b45]">User Accounts</h3>
        <button data-testid="add-user-btn" onClick={() => { setForm(empty); setModal("new"); }} className="bg-[#4e73df] hover:bg-[#3a5fd0] text-white text-sm font-semibold px-3.5 py-2 rounded-lg flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead><tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100">
            <th className="py-2 pr-3">Username</th><th className="py-2 px-3">Department</th><th className="py-2 px-3">Role</th><th className="py-2 px-3">Phone</th><th className="py-2 px-3">Status</th><th className="py-2 px-3 text-right">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-50">
            {users.map((u) => (
              <tr key={u.id}>
                <td className="py-2.5 pr-3 font-medium text-slate-700">{u.username}</td>
                <td className="py-2.5 px-3 text-slate-500">{u.department || "-"}</td>
                <td className="py-2.5 px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-[#4e73df]/10 text-[#4e73df]" : "bg-slate-100 text-slate-500"}`}>{roleLabel(u.role)}</span></td>
                <td className="py-2.5 px-3 text-slate-500">{u.phone || "-"}</td>
                <td className="py-2.5 px-3">{u.active ? <span className="text-emerald-600 text-xs">Active</span> : <span className="text-red-500 text-xs">Disabled</span>}</td>
                <td className="py-2.5 px-3 text-right whitespace-nowrap">
                  <button onClick={() => { setForm({ ...u, password: "" }); setModal("edit"); }} className="p-1.5 hover:bg-slate-100 rounded text-slate-500" data-testid={`edit-user-${u.username}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => del(u.id)} className="p-1.5 hover:bg-red-50 rounded text-red-500" data-testid={`delete-user-${u.username}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] gm-shadow-lg flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
              <h3 className="font-bold">{modal === "new" ? "Add User" : "Edit User"}</h3>
              <button onClick={() => setModal(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <div><label className={lbl}>Username</label><input className={field} value={form.username} data-testid="user-username" onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
              <div><label className={lbl}>Password {modal === "edit" && <span className="text-slate-400 font-normal">(leave blank to keep)</span>}</label><input type="password" className={field} value={form.password} data-testid="user-password" onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div><label className={lbl}>Department</label>
                <select className={field} value={form.department} data-testid="user-department" onChange={(e) => setForm({ ...form, department: e.target.value })}>
                  <option value="">None</option>{departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Email</label><input className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><label className={lbl}>Phone (OTP)</label><input className={field} value={form.phone} placeholder="+960..." onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3 items-center">
                <div><label className={lbl}>Role</label>
                  <select className={field} value={form.role} data-testid="user-role" onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    <option value="admin">Administrator</option>
                    {roles.filter((r) => r.name !== "admin").map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
                  </select></div>
                <div className="flex items-center gap-2 pt-6"><Toggle checked={form.active} onChange={(v) => setForm({ ...form, active: v })} testid="user-active" /><span className="text-sm text-slate-600">Active</span></div>
              </div>
            </div>
            <div className="p-5 pt-3 border-t border-slate-100 shrink-0">
              <button onClick={save} data-testid="save-user-btn" className="w-full bg-[#4e73df] hover:bg-[#3a5fd0] text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Save User</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ---------------- Departments ---------------- */
function DepartmentsTab() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const load = () => api.get("/departments").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name) return; try { await api.post("/departments", { name, description: desc }); setName(""); setDesc(""); load(); toast.success("Added"); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };
  const del = async (id) => { await api.delete(`/departments/${id}`); load(); };
  return (
    <div className={card}>
      <h3 className="font-bold text-[#3a3b45] mb-4">Departments / Locations</h3>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input className={field} placeholder="Department name" value={name} data-testid="dept-name" onChange={(e) => setName(e.target.value)} />
        <input className={field} placeholder="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} />
        <button onClick={add} data-testid="add-dept-btn" className="bg-[#4e73df] hover:bg-[#3a5fd0] text-white text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center gap-1.5 shrink-0"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {rows.map((d) => (
          <div key={d.id} className="flex items-center justify-between px-4 py-3 rounded-lg border border-slate-100 bg-slate-50/50">
            <div><p className="font-medium text-slate-700 text-sm">{d.name}</p><p className="text-xs text-slate-400">{d.description}</p></div>
            <button onClick={() => del(d.id)} className="p-1.5 hover:bg-red-50 rounded text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Classifications ---------------- */
function ClassificationsTab() {
  const [departments, setDepartments] = useState([]);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ department: "", sub_location: "", f_class: "" });
  const load = () => api.get("/classifications").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/departments").then(({ data }) => setDepartments(data)); }, []);
  const add = async () => { if (!f.department || !f.sub_location) { toast.error("Department & sub-location required"); return; } await api.post("/classifications", f); setF({ ...f, sub_location: "", f_class: "" }); load(); toast.success("Added"); };
  const del = async (id) => { await api.delete(`/classifications/${id}`); load(); };
  return (
    <div className={card}>
      <h3 className="font-bold text-[#3a3b45] mb-1">Sub-Locations & Forbes Classifications</h3>
      <p className="text-xs text-slate-400 mb-4">Location → Sub-Location → Forbes Classification (drives the cascading dropdowns on the glitch form)</p>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-4">
        <select className={field} value={f.department} data-testid="cls-dept" onChange={(e) => setF({ ...f, department: e.target.value })}>
          <option value="">Location</option>{departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
        </select>
        <input className={field} placeholder="Sub-Location" value={f.sub_location} data-testid="cls-sub" onChange={(e) => setF({ ...f, sub_location: e.target.value })} />
        <input className={field} placeholder="Forbes Classification" value={f.f_class} data-testid="cls-class" onChange={(e) => setF({ ...f, f_class: e.target.value })} />
        <button onClick={add} data-testid="add-cls-btn" className="bg-[#4e73df] hover:bg-[#3a5fd0] text-white text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center justify-center gap-1.5"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100"><th className="py-2 pr-3">Location</th><th className="py-2 px-3">Sub-Location</th><th className="py-2 px-3">Forbes Class</th><th className="py-2 px-3"></th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((r) => (
              <tr key={r.id}><td className="py-2 pr-3 text-slate-600">{r.department}</td><td className="py-2 px-3 text-slate-600">{r.sub_location}</td><td className="py-2 px-3 text-slate-500">{r.f_class || "-"}</td>
                <td className="py-2 px-3 text-right"><button onClick={() => del(r.id)} className="p-1.5 hover:bg-red-50 rounded text-red-500"><Trash2 className="w-4 h-4" /></button></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Notifications ---------------- */
function NotificationsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [smsTesting, setSmsTesting] = useState(false);
  const [tgTesting, setTgTesting] = useState(false);
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#4e73df]" /></div>;
  const upd = (sec, k, v) => setS({ ...s, [sec]: { ...s[sec], [k]: v } });
  const save = async () => { setSaving(true); try { await api.put("/settings", s); toast.success("Notification settings saved"); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); } };
  const test = async () => { setTesting(true); try { const { data } = await api.post("/settings/test-notification"); toast.success("Test dispatched — check your channels"); console.log(data); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setTesting(false); } };
  const sendTestTelegram = async () => {
    setTgTesting(true);
    try {
      const { data } = await api.post("/settings/test-telegram");
      if (data.ok) toast.success("Test Telegram message sent — check the group");
      else if (data.skipped) toast.warning("Enter & save the Bot Token and Group Chat ID first");
      else toast.error(`Telegram failed: ${data.error || data.status || "unknown"}`);
      console.log(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setTgTesting(false); }
  };
  const sendTestSms = async () => {
    if (!testPhone) { toast.error("Enter a phone number"); return; }
    setSmsTesting(true);
    try {
      const { data } = await api.post("/settings/test-sms", { phone: testPhone });
      if (data.ok) toast.success("Test SMS sent successfully");
      else if (data.skipped) toast.warning("Configure & save Dhiraagu username/password first");
      else toast.error(`SMS failed: ${data.desc || data.error || data.status || "unknown"}`);
      console.log(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSmsTesting(false); }
  };
  const listStr = (arr) => (arr || []).join(", ");
  const toList = (str) => str.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="space-y-5">
      {/* Telegram */}
      <div className={card} data-testid="telegram-settings">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#3a3b45] flex items-center gap-2"><Send className="w-5 h-5 text-[#229ED9]" /> Telegram Bot</h3>
          <Toggle checked={s.telegram.enabled} onChange={(v) => upd("telegram", "enabled", v)} testid="telegram-enabled" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Bot Token</label><input className={field} type="password" value={s.telegram.bot_token} data-testid="telegram-token" onChange={(e) => upd("telegram", "bot_token", e.target.value)} placeholder="From @BotFather" /></div>
          <div><label className={lbl}>Group Chat ID</label><input className={field} value={s.telegram.chat_id} data-testid="telegram-chatid" onChange={(e) => upd("telegram", "chat_id", e.target.value)} placeholder="-1001995465838" /></div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button onClick={sendTestTelegram} disabled={tgTesting} data-testid="test-telegram-btn" className="border border-[#229ED9] text-[#229ED9] hover:bg-[#229ED9]/5 font-semibold px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">{tgTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Test Message</button>
          <p className="text-[11px] text-slate-400">Save settings first so the test uses the token/chat ID above.</p>
        </div>
      </div>

      {/* WhatsApp */}
      <div className={card} data-testid="whatsapp-settings">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#3a3b45] flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#25D366]" /> WhatsApp Cloud API</h3>
          <Toggle checked={s.whatsapp.enabled} onChange={(v) => upd("whatsapp", "enabled", v)} testid="whatsapp-enabled" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Phone Number ID</label><input className={field} value={s.whatsapp.phone_number_id} data-testid="whatsapp-phoneid" onChange={(e) => upd("whatsapp", "phone_number_id", e.target.value)} /></div>
          <div><label className={lbl}>Access Token</label><input className={field} type="password" value={s.whatsapp.token} data-testid="whatsapp-token" onChange={(e) => upd("whatsapp", "token", e.target.value)} /></div>
          <div><label className={lbl}>API Version</label><input className={field} value={s.whatsapp.api_version} onChange={(e) => upd("whatsapp", "api_version", e.target.value)} /></div>
          <div><label className={lbl}>Recipients (comma separated, +E.164)</label><input className={field} value={listStr(s.whatsapp.recipients)} data-testid="whatsapp-recipients" onChange={(e) => upd("whatsapp", "recipients", toList(e.target.value))} placeholder="+9607xxxxxxx, +9607yyyyyyy" /></div>
          <div className="flex items-center gap-2"><Toggle checked={s.whatsapp.use_text} onChange={(v) => upd("whatsapp", "use_text", v)} /><span className="text-sm text-slate-600">Send free-text (off = use template)</span></div>
          {!s.whatsapp.use_text && (<>
            <div><label className={lbl}>Template Name</label><input className={field} value={s.whatsapp.template_name} onChange={(e) => upd("whatsapp", "template_name", e.target.value)} /></div>
            <div><label className={lbl}>Template Language</label><input className={field} value={s.whatsapp.template_language} onChange={(e) => upd("whatsapp", "template_language", e.target.value)} /></div>
          </>)}
        </div>
      </div>

      {/* Email */}
      <div className={card} data-testid="email-settings">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#3a3b45] flex items-center gap-2"><Mail className="w-5 h-5 text-[#4e73df]" /> Email (SMTP)</h3>
          <Toggle checked={s.email.enabled} onChange={(v) => upd("email", "enabled", v)} testid="email-enabled" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={lbl}>SMTP Host</label><input className={field} value={s.email.smtp_host} onChange={(e) => upd("email", "smtp_host", e.target.value)} /></div>
          <div><label className={lbl}>SMTP Port</label><input className={field} type="number" value={s.email.smtp_port} onChange={(e) => upd("email", "smtp_port", parseInt(e.target.value) || 587)} /></div>
          <div><label className={lbl}>Username</label><input className={field} value={s.email.username} onChange={(e) => upd("email", "username", e.target.value)} /></div>
          <div><label className={lbl}>Password</label><input className={field} type="password" value={s.email.password} onChange={(e) => upd("email", "password", e.target.value)} /></div>
          <div><label className={lbl}>From Email</label><input className={field} value={s.email.from_email} onChange={(e) => upd("email", "from_email", e.target.value)} /></div>
          <div><label className={lbl}>From Name</label><input className={field} value={s.email.from_name} onChange={(e) => upd("email", "from_name", e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={lbl}>Recipients (comma separated)</label><input className={field} value={listStr(s.email.recipients)} data-testid="email-recipients" onChange={(e) => upd("email", "recipients", toList(e.target.value))} placeholder="gm@hurawalhi.com, fom@hurawalhi.com" /></div>
          <div className="flex items-center gap-2"><Toggle checked={s.email.use_tls} onChange={(v) => upd("email", "use_tls", v)} /><span className="text-sm text-slate-600">Use STARTTLS</span></div>
        </div>
      </div>

      {/* OTP */}
      <div className={card} data-testid="otp-settings">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#3a3b45] flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#845adf]" /> SMS OTP — Dhiraagu BulkSMS</h3>
          <Toggle checked={s.otp.enabled} onChange={(v) => upd("otp", "enabled", v)} testid="otp-enabled" />
        </div>
        <p className="text-xs text-slate-400 mb-3">When enabled, users with a phone number must verify a one-time SMS code (sent via Dhiraagu) at login.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Account Username</label><input className={field} value={s.otp.username} data-testid="otp-username" onChange={(e) => upd("otp", "username", e.target.value)} placeholder="Provided by Dhiraagu" /></div>
          <div><label className={lbl}>Account Password</label><input className={field} type="password" value={s.otp.password} data-testid="otp-password" onChange={(e) => upd("otp", "password", e.target.value)} /></div>
          <div><label className={lbl}>Base URL</label><input className={field} value={s.otp.base_url} onChange={(e) => upd("otp", "base_url", e.target.value)} /></div>
          <div><label className={lbl}>Endpoint</label><input className={field} value={s.otp.endpoint} onChange={(e) => upd("otp", "endpoint", e.target.value)} /></div>
          <div><label className={lbl}>Country Code</label><input className={field} value={s.otp.country_code} onChange={(e) => upd("otp", "country_code", e.target.value)} placeholder="960" /></div>
          <div><label className={lbl}>Code Expiry (minutes)</label><input className={field} type="number" value={s.otp.otp_expiry_minutes} onChange={(e) => upd("otp", "otp_expiry_minutes", parseInt(e.target.value) || 5)} /></div>
          <div className="sm:col-span-2"><label className={lbl}>Message Template</label><input className={field} value={s.otp.message_template} onChange={(e) => upd("otp", "message_template", e.target.value)} /><p className="text-[11px] text-slate-400 mt-1">Placeholders: {"{app}"}, {"{code}"}, {"{mins}"}</p></div>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input className={field} value={testPhone} data-testid="test-sms-phone" onChange={(e) => setTestPhone(e.target.value)} placeholder="Test phone e.g. 7xxxxxx or 9607xxxxxx" />
          <button onClick={sendTestSms} disabled={smsTesting} data-testid="test-sms-btn" className="border border-[#845adf] text-[#845adf] hover:bg-[#845adf]/5 font-semibold px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60 shrink-0">{smsTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Test SMS</button>
        </div>
        <p className="text-[11px] text-amber-600 mt-2">Save settings before sending a test SMS.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={save} disabled={saving} data-testid="save-notifications-btn" className="flex-1 bg-[#4e73df] hover:bg-[#3a5fd0] text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save All Settings</button>
        <button onClick={test} disabled={testing} data-testid="test-notification-btn" className="flex-1 border border-[#4e73df] text-[#4e73df] hover:bg-[#4e73df]/5 font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">{testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Test Notification</button>
      </div>
    </div>
  );
}

/* ---------------- Alert Routing ---------------- */
const EVENT_ROWS = [
  { key: "created", label: "Created", hint: "A new glitch is reported" },
  { key: "updated", label: "Updated", hint: "Status, priority, comments, informed to/by change" },
  { key: "closed", label: "Closed", hint: "The glitch is resolved / closed" },
];

function EventToggleRow({ audience, s, upd }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {EVENT_ROWS.map((ev) => (
        <div key={ev.key} className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg px-3 py-2.5 bg-slate-50/60">
          <div>
            <p className="text-sm font-semibold text-[#3a3b45]">{ev.label}</p>
            <p className="text-[11px] text-slate-400">{ev.hint}</p>
          </div>
          <Toggle
            checked={!!s.alert_routing[audience][ev.key]}
            onChange={(v) => upd(audience, ev.key, v)}
            testid={`alert-${audience}-${ev.key}`}
          />
        </div>
      ))}
    </div>
  );
}

function AlertRoutingTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setS({
        ...data,
        alert_routing: {
          department_events: { created: true, updated: false, closed: true, ...(data.alert_routing?.department_events || {}) },
          executive_events: { created: true, updated: false, closed: true, ...(data.alert_routing?.executive_events || {}) },
          executive_recipients: data.alert_routing?.executive_recipients || [],
        },
      });
    });
  }, []);
  if (!s) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#4e73df]" /></div>;

  const upd = (audience, key, v) =>
    setS({ ...s, alert_routing: { ...s.alert_routing, [audience]: { ...s.alert_routing[audience], [key]: v } } });
  const listStr = (arr) => (arr || []).join(", ");
  const toList = (str) => str.split(",").map((x) => x.trim()).filter(Boolean);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { alert_routing: s.alert_routing });
      toast.success("Alert routing saved");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className={card}>
        <h3 className="font-bold text-[#3a3b45] mb-1">How email alerts are routed</h3>
        <p className="text-sm text-slate-500 leading-relaxed">
          Every glitch email goes out per-event (created / updated / closed) to whichever audiences below are
          switched on for that event. <b>Department</b> recipients are found automatically — any active user
          whose Department field matches the glitch's location, with an email on file. No list to maintain by
          hand. <b>Executive / Master</b> recipients are a fixed list you set here, independent of department,
          for management who should see activity across all locations. Telegram and WhatsApp are unaffected —
          those still broadcast to their configured groups on creation only.
        </p>
      </div>

      <div className={card} data-testid="alert-dept-settings">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-[#4e73df]" />
          <h3 className="font-bold text-[#3a3b45]">Department Alerts</h3>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Sent to active users whose Department matches the glitch's location. Pick which events trigger it.
        </p>
        <EventToggleRow audience="department_events" s={s} upd={upd} />
      </div>

      <div className={card} data-testid="alert-exec-settings">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-[#845adf]" />
          <h3 className="font-bold text-[#3a3b45]">Executive / Master Alerts</h3>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          A fixed recipient list — e.g. GM, FOM, or ownership — that gets alerts regardless of department.
        </p>
        <EventToggleRow audience="executive_events" s={s} upd={upd} />
        <div className="mt-3">
          <label className={lbl}>Executive Recipients (comma separated)</label>
          <input
            className={field}
            value={listStr(s.alert_routing.executive_recipients)}
            data-testid="alert-exec-recipients"
            onChange={(e) => setS({ ...s, alert_routing: { ...s.alert_routing, executive_recipients: toList(e.target.value) } })}
            placeholder="gm@hurawalhi.com, owner@company.com"
          />
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="save-alert-routing-btn"
        className="w-full bg-[#4e73df] hover:bg-[#3a5fd0] text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Alert Routing
      </button>
    </div>
  );
}

/* ---------------- Opera XML ---------------- */
function OperaTab() {
  const [count, setCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/opera/count").then(({ data }) => setCount(data.count));
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/opera/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Imported ${data.imported} guest records`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className={card}>
      <h3 className="font-bold text-[#3a3b45] mb-1 flex items-center gap-2"><FileUp className="w-5 h-5 text-[#4e73df]" /> Opera PMS Guest Data</h3>
      <p className="text-xs text-slate-400 mb-3">Upload the Opera export (<b>.xml</b> or delimited <b>.txt</b>) to power the room / guest auto-lookup on the glitch form.</p>
      <div className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4 space-y-1">
        <p className="font-semibold text-[#4e73df]">How to generate the TXT export in Opera</p>
        <p>When running the report, set <b>File Format: DELIMITED DATA</b> and <b>Delimiter: Tab</b> (Pipe <code>|</code> also works), then save the <code>.txt</code> file and upload it here.</p>
      </div>
      <div className="flex items-center gap-2 mb-5 px-4 py-3 rounded-lg bg-emerald-50 text-emerald-700 text-sm"><CheckCircle2 className="w-4 h-4" /> {count} guest record{count !== 1 ? "s" : ""} currently loaded</div>
      <label className="block border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-[#4e73df] transition-colors" data-testid="opera-upload-label">
        {uploading ? <Loader2 className="w-8 h-8 animate-spin text-[#4e73df] mx-auto" /> : <UploadCloud className="w-8 h-8 text-slate-400 mx-auto" />}
        <p className="mt-2 text-sm font-medium text-slate-600">Click to upload Opera export (.xml or .txt)</p>
        <p className="text-xs text-slate-400">Replaces existing guest data</p>
        <input type="file" accept=".xml,.txt,text/plain,text/xml" className="hidden" onChange={upload} data-testid="opera-upload-input" />
      </label>
      <AutomationKeyPanel />
    </div>
  );
}

/* ---------------- Opera upload automation key ---------------- */
function AutomationKeyPanel() {
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [open, setOpen] = useState(false);
  const apiBase = (window.location.origin) + "/api";

  const load = () => api.get("/settings/automation").then(({ data }) => setKey(data.opera_upload_key || "")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    if (key && !window.confirm("This will invalidate the current key — any scheduled script using it will stop working until updated. Continue?")) return;
    setBusy(true);
    try { const { data } = await api.post("/settings/automation/regenerate-key"); setKey(data.opera_upload_key); setReveal(true); toast.success("New key generated"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

  const script = key ? `# Windows Task Scheduler script - run every 30 min.
# Uploads the newest Opera export file from a folder to Glitch Manager automatically.
# Plain ASCII only in this file on purpose - copy/paste through some editors can
# mangle curly quotes or dashes and break the PowerShell parser.

$ExportFolder = "C:\\OperaExports"
$ApiUrl       = "${apiBase}/opera/upload"
$UploadKey    = "${key}"
$LogFile      = Join-Path $ExportFolder "upload-log.txt"

function Write-Log {
    param([string]$Message)
    $stamp = Get-Date -Format "yyyy-MM-dd HH:mm:ss"
    $line = "$stamp  $Message"
    Write-Output $line
    Add-Content -Path $LogFile -Value $line
}

$latest = Get-ChildItem -Path $ExportFolder -Include *.txt, *.xml -Recurse |
    Sort-Object LastWriteTime -Descending |
    Select-Object -First 1

if (-not $latest) {
    Write-Log "SKIPPED - no txt or xml file found in export folder"
    exit 1
}

$ageMinutes = (New-TimeSpan -Start $latest.LastWriteTime -End (Get-Date)).TotalMinutes
$ageRounded = [math]::Round($ageMinutes, 1)

try {
    Add-Type -AssemblyName System.Net.Http

    $httpClient = New-Object System.Net.Http.HttpClient
    $httpClient.DefaultRequestHeaders.Add("X-Upload-Key", $UploadKey)

    $fileStream = [System.IO.File]::OpenRead($latest.FullName)
    $fileContent = New-Object System.Net.Http.StreamContent($fileStream)
    $multipart = New-Object System.Net.Http.MultipartFormDataContent
    $multipart.Add($fileContent, "file", $latest.Name)

    $response = $httpClient.PostAsync($ApiUrl, $multipart).Result
    $responseText = $response.Content.ReadAsStringAsync().Result
    $fileStream.Close()

    if (-not $response.IsSuccessStatusCode) {
        throw "HTTP $([int]$response.StatusCode) - $responseText"
    }

    $result = $responseText | ConvertFrom-Json
    $importedCount = $result.imported
    Write-Log "OK - uploaded file, age $ageRounded minutes, imported $importedCount guest records"
}
catch {
    $errMsg = $_.Exception.Message
    Write-Log "FAILED - $errMsg"
    exit 1
}` : "";

  return (
    <div className="mt-6 border-t border-slate-100 pt-5">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left" data-testid="automation-panel-toggle">
        <span>
          <h3 className="font-bold text-[#3a3b45] mb-1 flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#4e73df]" /> Automated Upload (no login needed)</h3>
          <p className="text-xs text-slate-400">Generate a key so a scheduled script (e.g. Windows Task Scheduler) can upload the latest Opera export automatically every 30 minutes, without storing an admin password anywhere.</p>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
      {open && (
      loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-[#4e73df] mt-3" />
      ) : (
        <div className="mt-3">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <code className="flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono break-all">
              {key ? (reveal ? key : "•".repeat(24) + key.slice(-6)) : "No key generated yet"}
            </code>
            {key && <button onClick={() => setReveal((r) => !r)} className="text-xs font-semibold text-slate-500 px-2 py-2">{reveal ? "Hide" : "Show"}</button>}
            {key && <button onClick={() => copy(key)} className="text-xs font-semibold text-[#4e73df] px-2 py-2">Copy</button>}
            <button onClick={regenerate} disabled={busy} className="flex items-center gap-1.5 text-xs font-semibold text-white bg-[#4e73df] hover:bg-[#3a5fd0] rounded-lg px-3 py-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} {key ? "Regenerate" : "Generate Key"}
            </button>
          </div>
          {key && (
            <div className="relative">
              <pre className="text-[11px] leading-relaxed bg-[#3a3b45] text-slate-100 rounded-lg p-4 overflow-x-auto"><code>{script}</code></pre>
              <button onClick={() => copy(script)} className="absolute top-2 right-2 text-[10px] font-semibold text-white bg-white/10 hover:bg-white/20 rounded px-2 py-1">Copy script</button>
            </div>
          )}
          <p className="text-xs text-slate-400 mt-2">Save that as <code>upload-opera.ps1</code> on the PC where Opera exports the file, set the folder path, then add it to Windows Task Scheduler on a 30-minute recurring trigger.</p>
        </div>
      )
      )}
    </div>
  );
}

/* ---------------- General ---------------- */
function GeneralTab({ onSaved }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#4e73df]" /></div>;
  const upd = (k, v) => setS({ ...s, general: { ...s.general, [k]: v } });
  const save = async () => { setSaving(true); try { await api.put("/settings", { general: s.general }); toast.success("General settings saved"); onSaved && onSaved(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); } };
  const changePw = async () => { if (!pw.current_password || !pw.new_password) return; try { await api.post("/auth/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };
  const g = s.general;
  return (
    <div className="space-y-5">
      <div className={card}>
        <h3 className="font-bold text-[#3a3b45] mb-4">Application Settings</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={lbl}>App Name</label><input className={field} value={g.app_name} data-testid="gen-appname" onChange={(e) => upd("app_name", e.target.value)} /></div>
          <div><label className={lbl}>Timezone</label><input className={field} value={g.timezone} onChange={(e) => upd("timezone", e.target.value)} /></div>
          <div><label className={lbl}>Escalation Minutes</label><input className={field} type="number" value={g.escalation_minutes} onChange={(e) => upd("escalation_minutes", parseInt(e.target.value) || 30)} /></div>
          <div><label className={lbl}>Logo URL</label><input className={field} value={g.logo_url} onChange={(e) => upd("logo_url", e.target.value)} /></div>
          <div><label className={lbl}>Telegram Group Link</label><input className={field} value={g.telegram_group_link} data-testid="gen-tglink" onChange={(e) => upd("telegram_group_link", e.target.value)} /></div>
          <div><label className={lbl}>WhatsApp Group Link</label><input className={field} value={g.whatsapp_group_link} data-testid="gen-walink" onChange={(e) => upd("whatsapp_group_link", e.target.value)} placeholder="https://chat.whatsapp.com/..." /></div>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-general-btn" className="mt-4 bg-[#4e73df] hover:bg-[#3a5fd0] text-white font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
      </div>

      <div className={card}>
        <h3 className="font-bold text-[#3a3b45] mb-4 flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#4e73df]" /> Change My Password</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Current Password</label><input className={field} type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></div>
          <div><label className={lbl}>New Password</label><input className={field} type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
        </div>
        <button onClick={changePw} data-testid="change-pw-btn" className="mt-4 border border-[#4e73df] text-[#4e73df] hover:bg-[#4e73df]/5 font-semibold px-5 py-2.5 rounded-lg">Update Password</button>
      </div>
    </div>
  );
}

/* ---------------- Audit Log ---------------- */
function AuditLogTab() {
  const { settings } = useAuth();
  const tz = settings?.general?.timezone || "Asia/Dhaka";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/audit-log").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const actionLabel = { "user.create": "Created user", "user.update": "Updated user", "user.delete": "Deleted user",
    "settings.update": "Updated settings", "report_schedule.create": "Created report schedule",
    "report_schedule.update": "Updated report schedule", "report_schedule.delete": "Deleted report schedule",
    "report_schedule.run_now": "Sent report now", "classification.create": "Added classification",
    "classification.delete": "Deleted classification", "role.create": "Created role",
    "role.update": "Updated role", "role.delete": "Deleted role" };
  return (
    <div className={card}>
      <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 mb-3"><History className="w-4 h-4 text-[#4e73df]" /> Who changed what, and when</p>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#4e73df]" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 text-center">No admin activity recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
              <th className="px-4 py-2">When</th><th className="px-4 py-2">Who</th><th className="px-4 py-2">Action</th><th className="px-4 py-2">Target</th><th className="px-4 py-2">Details</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{formatDT(r.created_at, tz)}</td>
                  <td className="px-4 py-2 font-semibold text-slate-700">{r.actor}</td>
                  <td className="px-4 py-2 text-slate-600">{actionLabel[r.action] || r.action}</td>
                  <td className="px-4 py-2 text-slate-600">{r.target || "-"}</td>
                  <td className="px-4 py-2 text-slate-400 text-xs">{r.details || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Roles ---------------- */
const PAGE_DEFS = [
  { key: "new_glitch", label: "New Glitch" },
  { key: "status", label: "Glitch Status" },
  { key: "closed", label: "Closed Today" },
  { key: "guest_log", label: "Guest Log" },
  { key: "reports", label: "Reports" },
];
const ADMIN_SECTION_DEFS = [
  { key: "classifications", label: "Classifications (Admin)" },
  { key: "audit", label: "Audit Log (Admin)" },
];

function RoleFormModal({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    name: initial.name || "", label: initial.label || "",
    pages: initial.pages || [], admin_sections: initial.admin_sections || [],
    read_only: initial.read_only || false,
  });
  const [saving, setSaving] = useState(false);
  const togglePage = (k) => setForm((f) => ({ ...f, pages: f.pages.includes(k) ? f.pages.filter((p) => p !== k) : [...f.pages, k] }));
  const toggleSection = (k) => setForm((f) => ({ ...f, admin_sections: f.admin_sections.includes(k) ? f.admin_sections.filter((p) => p !== k) : [...f.admin_sections, k] }));
  const save = async () => {
    if (isNew && !/^[a-z][a-z0-9_]{1,39}$/.test(form.name)) { toast.error("Role name: lowercase letters/numbers/underscore, starting with a letter"); return; }
    if (!form.label.trim()) { toast.error("Give the role a display label"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/roles", form);
      else await api.put(`/roles/${initial.id}`, form);
      toast.success("Role saved");
      onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] gm-shadow-lg flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="role-modal">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
          <h3 className="font-bold">{isNew ? "New Role" : `Edit Role — ${initial.label}`}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          {isNew && (
            <div><label className={lbl}>Role Slug (used internally, can't change later)</label>
              <input className={field} data-testid="role-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })} placeholder="e.g. supervisor" /></div>
          )}
          <div><label className={lbl}>Display Label</label>
            <input className={field} data-testid="role-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Department Head" /></div>

          <div className="flex items-center gap-2">
            <Toggle checked={form.read_only} onChange={(v) => setForm({ ...form, read_only: v })} testid="role-readonly" />
            <span className="text-sm text-slate-600">Read-only — can view granted pages but not create, edit or close anything</span>
          </div>

          <div>
            <label className={lbl}>Pages this role can see</label>
            <div className="grid grid-cols-2 gap-2">
              {PAGE_DEFS.map((p) => (
                <label key={p.key} className="flex items-center gap-2 text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-2">
                  <input type="checkbox" checked={form.pages.includes(p.key)} onChange={() => togglePage(p.key)} /> {p.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className={lbl}>Admin panel sections (optional, beyond nav pages)</label>
            <div className="grid grid-cols-1 gap-2">
              {ADMIN_SECTION_DEFS.map((s) => (
                <label key={s.key} className="flex items-center gap-2 text-sm text-slate-600 border border-slate-200 rounded-lg px-3 py-2">
                  <input type="checkbox" checked={form.admin_sections.includes(s.key)} onChange={() => toggleSection(s.key)} /> {s.label}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Everything else in Admin (Users, Departments, Notifications, Alert Routing, Opera automation, General, Roles) stays administrator-only.</p>
          </div>
        </div>
        <div className="p-5 pt-3 border-t border-slate-100 shrink-0">
          <button onClick={save} disabled={saving} data-testid="save-role-btn" className="w-full bg-[#4e73df] hover:bg-[#3a5fd0] text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Role
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { id?, name?, label?, pages?, admin_sections?, read_only? } or null
  const load = () => api.get("/roles").then(({ data }) => setRoles(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const del = async (r) => {
    if (!window.confirm(`Delete the "${r.label}" role? Users on it must be reassigned first.`)) return;
    try { await api.delete(`/roles/${r.id}`); toast.success("Role deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const pageLabels = (keys) => (keys.length ? keys.map((k) => PAGE_DEFS.find((p) => p.key === k)?.label || k).join(", ") : "None");
  const sectionLabels = (keys) => (keys.length ? keys.map((k) => ADMIN_SECTION_DEFS.find((s) => s.key === k)?.label || k).join(", ") : "None");

  return (
    <div className={card}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-[#3a3b45]">Custom Roles</h3>
        <button data-testid="add-role-btn" onClick={() => setModal({ pages: [], admin_sections: [], read_only: false })} className="bg-[#4e73df] hover:bg-[#3a5fd0] text-white text-sm font-semibold px-3.5 py-2 rounded-lg flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> New Role
        </button>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Create as many roles as you need and pick which pages each one sees — e.g. a Supervisor for a department head,
        or a read-only Viewer for management. Assign users to a role from the Users tab.
      </p>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#4e73df]" /></div>
      ) : (
        <div className="space-y-2.5">
          {roles.map((r) => (
            <div key={r.id} className="border border-slate-100 rounded-xl px-4 py-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-[#3a3b45]">{r.label}</span>
                  <span className="text-[10px] font-mono text-slate-400">{r.name}</span>
                  {r.is_system && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Built-in</span>}
                  {r.read_only && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Read-only</span>}
                </div>
                <p className="text-xs text-slate-500 mt-1"><b>Pages:</b> {pageLabels(r.pages)}</p>
                {r.admin_sections.length > 0 && <p className="text-xs text-slate-500 mt-0.5"><b>Admin sections:</b> {sectionLabels(r.admin_sections)}</p>}
              </div>
              {!r.is_system && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setModal(r)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500" data-testid={`edit-role-${r.name}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => del(r)} className="p-1.5 hover:bg-red-50 rounded text-red-500" data-testid={`delete-role-${r.name}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {modal && <RoleFormModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const grantedSections = user?.permissions?.admin_sections || [];
  const visibleTabs = isAdmin ? TABS : TABS.filter((t) => !t.adminOnly && grantedSections.includes(t.id));
  const [tab, setTab] = useState(visibleTabs[0]?.id || "users");

  if (visibleTabs.length === 0) {
    return (
      <div className="gm-fade-up">
        <h2 className="font-bold text-lg text-[#3a3b45] mb-4">Admin Panel</h2>
        <div className={card}><p className="text-sm text-slate-500">No admin sections are enabled for your account.</p></div>
      </div>
    );
  }

  return (
    <div className="gm-fade-up">
      <h2 className="font-bold text-lg text-[#3a3b45] mb-4">Admin Panel</h2>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-5 -mx-1 px-1">
        {visibleTabs.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? "bg-[#4e73df] text-white gm-shadow" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "users" && <UsersTab />}
      {tab === "departments" && <DepartmentsTab />}
      {tab === "classifications" && <ClassificationsTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "alertrouting" && <AlertRoutingTab />}
      {tab === "opera" && <OperaTab />}
      {tab === "general" && <GeneralTab onSaved={() => window.location.reload()} />}
      {tab === "roles" && <RolesTab />}
      {tab === "audit" && <AuditLogTab />}
    </div>
  );
}
